import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { api } from "../lib/api";
import { useAuth } from "../shell/AuthProvider";
import { useAccess } from "../shell/AccessProvider";
import { Spotlight } from "./Spotlight";
import {
  isAccountMenuTarget,
  nextOnboardingId,
  playbookHref,
  primaryPlaybookId,
  visiblePlaybooks,
  type Playbook,
  type PlaybookId,
  type PlaybookStep,
} from "./playbooks";
import { scrollTargetIntoView, stickyHeaderBottom, targetInView } from "./scrollTarget";
import {
  applyProgress,
  completedTourIds,
  loadPlaybookStore,
  mergePlaybookStores,
  savePlaybookStore,
  shouldAutoStart,
  toursDiffer,
} from "./storage";

interface GuideState {
  available: Playbook[];
  start: (id?: PlaybookId) => void;
  replay: (id?: PlaybookId) => void;
  step: PlaybookStep | null;
}

const GuideContext = createContext<GuideState | null>(null);

function findTourNode(target: string | undefined): HTMLElement | null {
  if (!target || typeof document === "undefined") return null;
  return document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
}

function nodeIsDisabled(node: HTMLElement | null): boolean {
  return Boolean(node && "disabled" in node && (node as HTMLButtonElement).disabled);
}

function isEmptyInput(node: HTMLElement | null): boolean {
  if (!node) return true;
  if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
    return !node.value.trim();
  }
  return false;
}

/** Steps inside the top bar, section nav or a tab row keep the card beside them; everything else floats top right. */
const DOCK_EXEMPT = "header, nav, [role='tablist']";

function stepSpot(step: PlaybookStep | null | undefined): string[] {
  if (!step) return [];
  if (step.spot?.length) return step.spot;
  return step.target ? [step.target] : [];
}

function readHole(targets: string[]): { top: number; left: number; width: number; height: number } | null {
  const rects = targets
    .map((target) => findTourNode(target)?.getBoundingClientRect())
    .filter((rect): rect is DOMRect => Boolean(rect && rect.width >= 2 && rect.height >= 2));
  if (!rects.length) return null;
  const top = Math.min(...rects.map((rect) => rect.top));
  const left = Math.min(...rects.map((rect) => rect.left));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));
  return { top, left, width: right - left, height: bottom - top };
}

function stepTargetReady(step: PlaybookStep | undefined): boolean {
  if (!step?.target) return true;
  if (isAccountMenuTarget(step.target)) return true;
  return Boolean(findTourNode(step.target));
}

/** Next is only for read-along / typed steps — click steps force the highlighted control. */
function nextAllowed(step: PlaybookStep | null): boolean {
  if (!step) return false;
  if (step.advanceOnClick) return false;
  if (step.requireFilled && step.target && isEmptyInput(findTourNode(step.target))) return false;
  return true;
}

export function GuideProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const { can, status, isTrial, accessBlocked } = useAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = session?.userId;
  const [playbookId, setPlaybookId] = useState<PlaybookId | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [picker, setPicker] = useState(false);
  const [intro, setIntro] = useState(false);
  const [hole, setHole] = useState<ReturnType<typeof readHole>>(null);
  const [nextEnabled, setNextEnabled] = useState(true);
  const [booted, setBooted] = useState(false);
  const [toursReady, setToursReady] = useState(false);
  // A locked run is required trial onboarding: no Exit, no Esc, no replay picker.
  const [locked, setLocked] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [docked, setDocked] = useState(false);
  // Set when a tour finishes: drives the "well done" card with star progress.
  const [done, setDone] = useState<PlaybookId | null>(null);
  const lockedRef = useRef(false);
  lockedRef.current = locked;
  const stepIndexRef = useRef(0);
  const clickAdvanceTimerRef = useRef<number | null>(null);
  const clickAdvanceFromRef = useRef<number | null>(null);
  const waitTimerRef = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  stepIndexRef.current = stepIndex;

  const available = useMemo(
    () => visiblePlaybooks({ isOrgAdmin: Boolean(session?.isOrgAdmin), can }),
    [can, session?.isOrgAdmin],
  );
  const ids = useMemo(() => available.map((item) => item.id), [available]);
  const playbook = available.find((item) => item.id === playbookId) ?? null;
  const step = !intro && playbook ? playbook.steps[stepIndex] ?? null : null;

  const clearWait = useCallback(() => {
    if (waitTimerRef.current != null) {
      window.clearInterval(waitTimerRef.current);
      waitTimerRef.current = null;
    }
  }, []);

  const persist = useCallback((
    id: PlaybookId,
    statusValue: "in_progress" | "completed" | "dismissed",
    stepValue?: number,
  ) => {
    if (!userId) return;
    const store = loadPlaybookStore(userId);
    const next = applyProgress(store, id, statusValue, stepValue);
    if (next === store) return;
    dirtyRef.current = true;
    savePlaybookStore(userId, next);
    void api.put("/auth/preferences/tours", { tours: next }).catch(() => undefined);
  }, [userId]);

  const go = useCallback((item: Playbook, index: number) => {
    const upcoming = item.steps[index];
    const href = upcoming ? playbookHref(upcoming, session?.orgSlug) : null;
    if (!href) return;
    const current = `${location.pathname}${location.search}`;
    if (href === current) return;
    // Stay on a company record when the step only names the companies list —
    // but only for an actual sub-path (e.g. /crm/companies/123), never an
    // exact pathname match with a different query string (e.g. clearing
    // ?tab=app back to the bare /settings must still navigate).
    if (!href.includes("?") && location.pathname !== href && location.pathname.startsWith(`${href}/`)) return;
    navigate(href);
  }, [location.pathname, location.search, navigate, session?.orgSlug]);

  const begin = useCallback((id: PlaybookId | undefined, lock: boolean) => {
    setBooted(true);
    if (clickAdvanceTimerRef.current != null) {
      window.clearTimeout(clickAdvanceTimerRef.current);
      clickAdvanceTimerRef.current = null;
    }
    clickAdvanceFromRef.current = null;
    clearWait();
    setStuck(false);
    setDone(null);
    setLocked(lock);
    lockedRef.current = lock;
    if (!id) {
      setPicker(true);
      setIntro(false);
      setPlaybookId(null);
      return;
    }
    const item = available.find((entry) => entry.id === id);
    if (!item) return;
    stepIndexRef.current = 0;
    setPicker(false);
    setIntro(true);
    setPlaybookId(id);
    setStepIndex(0);
    persist(id, "in_progress", 0);
    go(item, 0);
  }, [available, clearWait, go, persist]);

  const start = useCallback((id?: PlaybookId) => {
    if (lockedRef.current) return;
    begin(id, false);
  }, [begin]);

  const dismissIntro = useCallback(() => {
    setIntro(false);
  }, []);

  const replay = useCallback((id?: PlaybookId) => {
    start(id ?? (available.length === 1 ? available[0].id : undefined));
  }, [available, start]);

  const exit = useCallback(() => {
    if (lockedRef.current) return;
    if (playbookId) persist(playbookId, "dismissed", stepIndex);
    if (clickAdvanceTimerRef.current != null) {
      window.clearTimeout(clickAdvanceTimerRef.current);
      clickAdvanceTimerRef.current = null;
    }
    clickAdvanceFromRef.current = null;
    clearWait();
    setPlaybookId(null);
    setIntro(false);
    setPicker(false);
  }, [clearWait, persist, playbookId, stepIndex]);

  const advance = useCallback(() => {
    if (!playbook || !playbookId) return;
    clearWait();
    const nextIndex = stepIndexRef.current + 1;
    if (nextIndex >= playbook.steps.length) {
      persist(playbookId, "completed", stepIndexRef.current);
      setLocked(false);
      lockedRef.current = false;
      setStuck(false);
      setPlaybookId(null);
      setIntro(false);
      setDone(playbookId);
      return;
    }
    const upcoming = playbook.steps[nextIndex];
    // Advance state and navigate immediately — go() is what can make the
    // upcoming target appear (a tab switch, a route change), so it must run
    // before we wait for that target, not after. Previously this waited for
    // stepTargetReady() before ever calling go(), which deadlocked any step
    // transition that needed navigation to reveal its own target (e.g. the
    // org-admin tour's Company details -> Add a person step, which needs
    // go() to switch the Settings tab back to Users).
    stepIndexRef.current = nextIndex;
    setStepIndex(nextIndex);
    persist(playbookId, "in_progress", nextIndex);
    go(playbook, nextIndex);
    if (stepTargetReady(upcoming)) return;
    // After Create company (async navigate), wait for Contacts — do not skip ahead.
    const started = Date.now();
    waitTimerRef.current = window.setInterval(() => {
      if (stepTargetReady(upcoming) || Date.now() - started > 12_000) {
        clearWait();
      }
    }, 150);
  }, [clearWait, go, persist, playbook, playbookId]);
  const advanceRef = useRef(advance);
  advanceRef.current = advance;

  const clearClickAdvance = useCallback(() => {
    if (clickAdvanceTimerRef.current != null) {
      window.clearTimeout(clickAdvanceTimerRef.current);
      clickAdvanceTimerRef.current = null;
    }
    clickAdvanceFromRef.current = null;
  }, []);

  // fromIndex must be the step that was clicked, so a second click inside the
  // 80ms window cannot advance a later step too.
  const scheduleClickAdvance = useCallback((fromIndex: number) => {
    if (clickAdvanceTimerRef.current != null && clickAdvanceFromRef.current === fromIndex) return;
    clearClickAdvance();
    clickAdvanceFromRef.current = fromIndex;
    clickAdvanceTimerRef.current = window.setTimeout(() => {
      clickAdvanceTimerRef.current = null;
      clickAdvanceFromRef.current = null;
      if (stepIndexRef.current === fromIndex) advanceRef.current();
    }, 80);
  }, [clearClickAdvance]);

  const next = useCallback(() => {
    if (intro || !nextAllowed(step)) return;
    advance();
  }, [advance, intro, step]);

  // Only reset when the signed-in user actually changes. A mount effect that
  // always set booted=false raced the auto-start effect (both run after the
  // first paint) and could leave the first test looking at a blank desk.
  const userIdRef = useRef(userId);
  useEffect(() => {
    if (userIdRef.current === userId) return;
    userIdRef.current = userId;
    dirtyRef.current = false;
    setBooted(false);
    setToursReady(false);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void (async () => {
      let remote: ReturnType<typeof loadPlaybookStore> | null = null;
      try {
        const body = await api.get<{ tours?: ReturnType<typeof loadPlaybookStore> }>("/auth/preferences/tours");
        remote = body?.tours && typeof body.tours === "object" ? body.tours : {};
      } catch {
        remote = null;
      }
      if (cancelled || dirtyRef.current) {
        if (!cancelled) setToursReady(true);
        return;
      }
      const merged = mergePlaybookStores(loadPlaybookStore(userId), remote);
      savePlaybookStore(userId, merged);
      if (remote && toursDiffer(merged, remote)) {
        void api.put("/auth/preferences/tours", { tours: merged }).catch(() => undefined);
      }
      if (!cancelled) setToursReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    // Auto-start is a trial-only feature, so wait until this user's access has
    // actually loaded and says trial. AccessProvider reports "ready" while
    // logged out, so booting on status alone ran once with isTrial still false
    // right after sign-in and never started the tour. Tour progress is loaded
    // from the account first, so a star earned on another browser is not
    // overwritten by an auto-start on this one.
    if (status !== "ready" || !userId || booted || !isTrial || !toursReady) return;
    setBooted(true);
    const store = loadPlaybookStore(userId);
    const primary = primaryPlaybookId(ids);
    const required = isTrial && session?.isOrgAdmin && !accessBlocked ? nextOnboardingId(store, ids) : null;
    if (required) {
      begin(required, true);
      return;
    }
    if (primary && isTrial && !accessBlocked && shouldAutoStart(store, primary)) {
      start(primary);
    }
  }, [accessBlocked, begin, booted, ids, isTrial, session?.isOrgAdmin, start, status, toursReady, userId]);

  useEffect(() => {
    if (intro || !step) {
      setNextEnabled(false);
      return;
    }
    const measure = () => setNextEnabled(nextAllowed(step));
    measure();
    const field = step.requireFilled && step.target ? findTourNode(step.target) : null;
    field?.addEventListener("input", measure);
    field?.addEventListener("change", measure);
    const timer = window.setInterval(measure, 200);
    return () => {
      field?.removeEventListener("input", measure);
      field?.removeEventListener("change", measure);
      window.clearInterval(timer);
    };
  }, [intro, step, stepIndex]);

  useEffect(() => {
    if (intro) {
      setHole(null);
      return;
    }
    const targets = stepSpot(step);
    if (!targets.length) {
      setHole(null);
      return;
    }
    // One scrollIntoView call, made before the form had a layout box, used to
    // no-op and never retry — Country and timezone, and the save buttons under
    // a long form, stayed below the fold. Retry only while the target is
    // actually off screen, and stop once it is in view so a later manual
    // scroll is left alone.
    let attempts = 0;
    const measure = (allowScroll: boolean) => {
      const node = findTourNode(step?.target) ?? findTourNode(targets[0]);
      const header = stickyHeaderBottom();
      setDocked(Boolean(node) && !node!.closest(DOCK_EXEMPT));
      if (allowScroll && node && attempts < 8 && !targetInView(node, header)) {
        const rect = node.getBoundingClientRect();
        if (rect.width >= 2 && rect.height >= 2) {
          scrollTargetIntoView(node, header);
          attempts += 1;
        }
      }
      setHole(readHole(targets));
    };
    measure(true);
    const timer = window.setInterval(() => measure(true), 300);
    const onResize = () => measure(true);
    const onScroll = () => measure(false);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [intro, step, location.pathname, location.search, stepIndex]);

  // Click-to-advance listens once on the document (capture phase) and matches the click against the
  // step that is on screen. Binding to the node itself lost clicks: the listener was attached by a
  // passive effect and re-attached by a 200ms poll, so a click before the effect ran, or on a node
  // React had just replaced, reached no listener and left the tour stuck on that step. The layout
  // effect sets the step before the browser paints it, so no click can arrive ahead of it.
  const clickStepRef = useRef<{ index: number; target: string } | null>(null);
  useLayoutEffect(() => {
    clickStepRef.current = !intro && step?.advanceOnClick && step.target ? { index: stepIndex, target: step.target } : null;
  }, [intro, step?.advanceOnClick, step?.target, stepIndex]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const current = clickStepRef.current;
      if (!current || !(event.target instanceof Element)) return;
      const node = event.target.closest<HTMLElement>(`[data-tour="${current.target}"]`);
      if (!node || nodeIsDisabled(node)) return;
      scheduleClickAdvance(current.index);
    };
    const onSubmit = (event: SubmitEvent) => {
      const current = clickStepRef.current;
      if (!current || !(event.target instanceof HTMLFormElement)) return;
      const submitter = event.submitter;
      const node = submitter instanceof Element
        ? submitter.closest<HTMLElement>(`[data-tour="${current.target}"]`)
        : event.target.querySelector<HTMLElement>(`[data-tour="${current.target}"]`);
      if (!node || nodeIsDisabled(node)) return;
      scheduleClickAdvance(current.index);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, [scheduleClickAdvance]);

  useEffect(() => () => {
    clearClickAdvance();
    clearWait();
  }, [clearClickAdvance, clearWait]);

  useEffect(() => {
    if (intro || !step?.target) {
      delete document.documentElement.dataset.tourTarget;
      return;
    }
    document.documentElement.dataset.tourTarget = step.target;
    const target = findTourNode(step.target);
    target?.classList.add("guide-active-target");
    return () => {
      target?.classList.remove("guide-active-target");
      delete document.documentElement.dataset.tourTarget;
    };
  }, [intro, location.pathname, location.search, step?.target]);

  // Never trap someone behind a tour once their trial access has ended —
  // AppShell sends them to billing and the overlay would block that page.
  useEffect(() => {
    if (!accessBlocked || !lockedRef.current) return;
    lockedRef.current = false;
    setLocked(false);
    setPlaybookId(null);
    setIntro(false);
  }, [accessBlocked]);

  // Lockout guards for a required tour: if the highlighted control is missing,
  // first re-open the step's own page, then (after a long wait) offer a skip.
  useEffect(() => {
    setStuck(false);
    if (!locked || intro || !step?.target || !playbook) return;
    if (isAccountMenuTarget(step.target)) return;
    const started = Date.now();
    let reopened = false;
    let offScreenSince: number | null = null;
    const timer = window.setInterval(() => {
      const node = findTourNode(step.target);
      if (!node) {
        offScreenSince = null;
        const waited = Date.now() - started;
        if (waited > 4_000 && !reopened && step.open) {
          reopened = true;
          go(playbook, stepIndexRef.current);
        }
        if (waited > 30_000) setStuck(true);
        return;
      }
      if (targetInView(node, stickyHeaderBottom())) {
        offScreenSince = null;
        setStuck(false);
        return;
      }
      if (offScreenSince == null) offScreenSince = Date.now();
      if (Date.now() - offScreenSince > 8_000) setStuck(true);
    }, 500);
    return () => window.clearInterval(timer);
  }, [go, intro, locked, playbook, step, stepIndex]);

  useEffect(() => {
    if (!playbook && !picker && !done) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || lockedRef.current) return;
      if (done) setDone(null);
      else exit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [done, exit, playbook, picker]);

  const doneCard = useMemo(() => {
    if (!done || !userId) return null;
    const store = loadPlaybookStore(userId);
    const offer = done === "org-admin"
      ? available.find((item) => item.id === "salesperson" && store.salesperson?.status !== "completed") ?? null
      : null;
    return {
      title: available.find((item) => item.id === done)?.title ?? "Guided tour",
      completed: completedTourIds(store, ids).length,
      total: ids.length,
      offer,
    };
  }, [available, done, ids, userId]);
  const completedIds = userId && (picker || done) ? completedTourIds(loadPlaybookStore(userId), ids) : [];

  const value = useMemo(() => ({ available, start, replay, step }), [available, replay, start, step]);

  return (
    <GuideContext.Provider value={value}>
      {children}
      {(playbook && (intro || step)) || picker || doneCard ? (
        <Spotlight
          hole={picker || intro ? null : hole}
          playbook={playbook}
          step={step}
          index={stepIndex}
          total={playbook?.steps.length ?? 0}
          onNext={next}
          onExit={exit}
          nextEnabled={nextEnabled}
          intro={intro}
          onIntroDone={dismissIntro}
          picker={picker ? available : null}
          onPick={start}
          locked={locked}
          onSkip={locked && !intro && (stuck || step?.skippable) ? advance : undefined}
          docked={docked}
          completedIds={completedIds}
          done={doneCard}
          onDoneStart={(id) => begin(id, false)}
          onDoneClose={() => setDone(null)}
        />
      ) : null}
    </GuideContext.Provider>
  );
}

export function useGuide() {
  const value = useContext(GuideContext);
  if (!value) throw new Error("useGuide requires GuideProvider");
  return value;
}

export function useOptionalGuide() {
  return useContext(GuideContext);
}
